import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Mail,
  Globe,
  Building,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';

const API_BASE = '/api';

// Types
type RuleType = 'email' | 'domain' | 'customer_code';
type RuleAction = 'allow' | 'block' | 'review';

interface AllowlistRule {
  id: string;
  ruleType: RuleType;
  emailPattern?: string;
  domain?: string;
  customerCode?: string;
  customerName?: string;
  action: RuleAction;
  priority: number;
  isActive: boolean;
  createdBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface AllowlistStats {
  total: number;
  active: number;
  byAction: Record<RuleAction, number>;
  byType: Record<RuleType, number>;
}

const actionConfig: Record<RuleAction, { icon: React.ElementType; color: string; bgColor: string; label: string }> = {
  allow: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Allow' },
  block: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Block' },
  review: { icon: AlertTriangle, color: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'Review' },
};

const ruleTypeConfig: Record<RuleType, { icon: React.ElementType; label: string }> = {
  email: { icon: Mail, label: 'Email Pattern' },
  domain: { icon: Globe, label: 'Domain' },
  customer_code: { icon: Building, label: 'Customer Code' },
};

export default function AllowlistPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AllowlistRule | null>(null);
  const [formData, setFormData] = useState({
    ruleType: 'email' as RuleType,
    emailPattern: '',
    domain: '',
    customerCode: '',
    customerName: '',
    action: 'allow' as RuleAction,
    priority: 0,
    notes: '',
  });

  // Fetch rules
  const { data: rulesData, isLoading, refetch } = useQuery<{ data: AllowlistRule[] }>({
    queryKey: ['allowlist-rules'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/allowlist`);
      if (!res.ok) throw new Error('Failed to fetch rules');
      return res.json();
    },
  });

  // Fetch stats
  const { data: statsData } = useQuery<{ data: AllowlistStats }>({
    queryKey: ['allowlist-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/allowlist/stats`);
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
  });

  // Create rule mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch(`${API_BASE}/allowlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create rule');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowlist-rules'] });
      queryClient.invalidateQueries({ queryKey: ['allowlist-stats'] });
      closeModal();
    },
  });

  // Update rule mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const res = await fetch(`${API_BASE}/allowlist/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update rule');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowlist-rules'] });
      queryClient.invalidateQueries({ queryKey: ['allowlist-stats'] });
      closeModal();
    },
  });

  // Toggle rule mutation
  const toggleMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/allowlist/${id}/toggle`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to toggle rule');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowlist-rules'] });
      queryClient.invalidateQueries({ queryKey: ['allowlist-stats'] });
    },
  });

  // Delete rule mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/allowlist/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete rule');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowlist-rules'] });
      queryClient.invalidateQueries({ queryKey: ['allowlist-stats'] });
    },
  });

  const rules = rulesData?.data || [];
  const stats = statsData?.data;

  function openCreateModal() {
    setEditingRule(null);
    setFormData({
      ruleType: 'email',
      emailPattern: '',
      domain: '',
      customerCode: '',
      customerName: '',
      action: 'allow',
      priority: 0,
      notes: '',
    });
    setShowModal(true);
  }

  function openEditModal(rule: AllowlistRule) {
    setEditingRule(rule);
    setFormData({
      ruleType: rule.ruleType,
      emailPattern: rule.emailPattern || '',
      domain: rule.domain || '',
      customerCode: rule.customerCode || '',
      customerName: rule.customerName || '',
      action: rule.action,
      priority: rule.priority,
      notes: rule.notes || '',
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingRule(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  }

  function handleDelete(rule: AllowlistRule) {
    if (confirm(`Are you sure you want to delete this rule?`)) {
      deleteMutation.mutate(rule.id);
    }
  }

  function getRulePattern(rule: AllowlistRule): string {
    switch (rule.ruleType) {
      case 'email': return rule.emailPattern || '';
      case 'domain': return rule.domain || '';
      case 'customer_code': return rule.customerCode || '';
      default: return '';
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Allowlist</h1>
          <p className="text-gray-500 mt-1">Control which senders/customers can process orders</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-dark"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-900">Allow All Mode Active</h3>
            <p className="text-sm text-blue-700 mt-1">
              By default, all emails are processed. Only emails matching "Block" rules will be rejected.
              Use "Review" rules to flag emails for manual inspection.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Rules</p>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Allow Rules</p>
                <p className="text-xl font-bold text-gray-900">{stats.byAction.allow}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Block Rules</p>
                <p className="text-xl font-bold text-gray-900">{stats.byAction.block}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Review Rules</p>
                <p className="text-xl font-bold text-gray-900">{stats.byAction.review}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rules List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-perfectted-primary" />
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No rules configured</h3>
            <p className="text-gray-500 mt-1">All emails are currently allowed by default</p>
            <button
              onClick={openCreateModal}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-dark"
            >
              <Plus className="w-4 h-4" />
              Add First Rule
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pattern</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rules.map((rule) => {
                    const action = actionConfig[rule.action];
                    const ActionIcon = action.icon;
                    const ruleType = ruleTypeConfig[rule.ruleType];
                    const TypeIcon = ruleType.icon;

                    return (
                      <tr key={rule.id} className={clsx('hover:bg-gray-50', !rule.isActive && 'opacity-50')}>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleMutation.mutate(rule.id)}
                            className={clsx(
                              'p-1 rounded',
                              rule.isActive ? 'text-green-600' : 'text-gray-400'
                            )}
                            title={rule.isActive ? 'Active - Click to disable' : 'Inactive - Click to enable'}
                          >
                            {rule.isActive ? (
                              <ToggleRight className="w-6 h-6" />
                            ) : (
                              <ToggleLeft className="w-6 h-6" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{ruleType.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{getRulePattern(rule)}</p>
                            {rule.customerName && (
                              <p className="text-xs text-gray-500">{rule.customerName}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={clsx('flex items-center gap-2 px-2 py-1 rounded-full w-fit', action.bgColor)}>
                            <ActionIcon className={clsx('w-4 h-4', action.color)} />
                            <span className={clsx('text-sm font-medium', action.color)}>{action.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{rule.priority}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600 truncate max-w-xs block">
                            {rule.notes || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal(rule)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(rule)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {rules.map((rule) => {
                const action = actionConfig[rule.action];
                const ActionIcon = action.icon;
                const ruleType = ruleTypeConfig[rule.ruleType];
                const TypeIcon = ruleType.icon;

                return (
                  <div key={rule.id} className={clsx('p-4', !rule.isActive && 'opacity-50')}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <TypeIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-900">{getRulePattern(rule)}</span>
                        </div>
                        <div className={clsx('flex items-center gap-2 px-2 py-1 rounded-full w-fit', action.bgColor)}>
                          <ActionIcon className={clsx('w-3 h-3', action.color)} />
                          <span className={clsx('text-xs font-medium', action.color)}>{action.label}</span>
                        </div>
                        {rule.notes && (
                          <p className="text-xs text-gray-500 mt-2">{rule.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <button
                          onClick={() => toggleMutation.mutate(rule.id)}
                          className={rule.isActive ? 'text-green-600' : 'text-gray-400'}
                        >
                          {rule.isActive ? (
                            <ToggleRight className="w-5 h-5" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={() => openEditModal(rule)}
                          className="p-1 text-gray-400"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule)}
                          className="p-1 text-gray-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingRule ? 'Edit Rule' : 'Add Rule'}
              </h2>
              <button onClick={closeModal} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Rule Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule Type</label>
                <select
                  value={formData.ruleType}
                  onChange={(e) => setFormData({ ...formData, ruleType: e.target.value as RuleType })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  disabled={!!editingRule}
                >
                  <option value="email">Email Pattern</option>
                  <option value="domain">Domain</option>
                  <option value="customer_code">Customer Code</option>
                </select>
              </div>

              {/* Pattern based on type */}
              {formData.ruleType === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Pattern</label>
                  <input
                    type="text"
                    value={formData.emailPattern}
                    onChange={(e) => setFormData({ ...formData, emailPattern: e.target.value })}
                    placeholder="orders@example.com or *@example.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Use * as wildcard (e.g., *@tesco.com)</p>
                </div>
              )}

              {formData.ruleType === 'domain' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="example.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    required
                  />
                </div>
              )}

              {formData.ruleType === 'customer_code' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Code</label>
                  <input
                    type="text"
                    value={formData.customerCode}
                    onChange={(e) => setFormData({ ...formData, customerCode: e.target.value })}
                    placeholder="CTSC001"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    required
                  />
                </div>
              )}

              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name (Optional)</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  placeholder="Tesco"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>

              {/* Action */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['allow', 'block', 'review'] as RuleAction[]).map((action) => {
                    const cfg = actionConfig[action];
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={action}
                        type="button"
                        onClick={() => setFormData({ ...formData, action })}
                        className={clsx(
                          'flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                          formData.action === action
                            ? `${cfg.bgColor} ${cfg.color} border-current`
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Higher priority rules are checked first</p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Why this rule exists..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg resize-none"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-dark disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
